import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Breadcrumbs, Container, Grid2, LinearProgress, Slider, TextField, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, TimeScale } from "chart.js";
import dayjs from "dayjs";
import annotationPlugin from "chartjs-plugin-annotation";
import "chartjs-adapter-date-fns";
import api from "./services/api";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, annotationPlugin, TimeScale);

const SurveyPace = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [progress, setProgress] = useState(0);
  const [totalResponses, setTotalResponses] = useState(0);
  const [completionTimes, setCompletionTimes] = useState([]);
  const [targetCompletes, setTargetCompletes] = useState(100);
  const [selectedTimeRange, setSelectedTimeRange] = useState([0, 100]);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));

    const interval = setInterval(() => {
      api.get(`/surveys/${surveyId}/fetch-progress/`).then((res) => {
        setProgress(res.data.progress);
        setTotalResponses(res.data.total);
        if (res.data.progress >= res.data.total && res.data.total > 0) {
          clearInterval(interval);
          setFetching(false);
        }
      });
    }, 2000);

    api.get(`/surveys/${surveyId}/response-dates/`).then((res) => {
      const sorted = res.data.timestamp
        .map((t) => new Date(t).getTime())
        .sort((a, b) => a - b)
        .map((t) => new Date(t).toISOString());
      setCompletionTimes(sorted);
    });

    return () => clearInterval(interval);
  }, [surveyId]);

  const processData = (data) => {
    const dateCount = {};
    data.forEach((dt) => {
      const key = `${dt.substring(0, 10)} ${dt.substring(11, 13)}:00`;
      dateCount[key] = (dateCount[key] || 0) + 1;
    });
    const labels = Object.keys(dateCount);
    let cum = 0;
    return {
      labels,
      datasets: [{ label: "Cumulative Responses", data: labels.map((l) => { cum += dateCount[l]; return cum; }), borderColor: "rgba(75,192,192,1)", fill: false, tension: 0.1 }],
    };
  };

  const timestamps = completionTimes.map((t) => new Date(t).getTime()).sort((a, b) => a - b);
  const startTs = timestamps[0] || Date.now();
  const endTs = timestamps[timestamps.length - 1] || Date.now();
  const responsesInRange = timestamps.filter((t) => t >= selectedTimeRange[0] && t <= selectedTimeRange[1]).length;

  const estimatedDays = useMemo(() => {
    if (!responsesInRange) return "Not enough data";
    const days = (selectedTimeRange[1] - selectedTimeRange[0]) / (24 * 3600 * 1000);
    const rate = responsesInRange / days;
    if (!rate) return "No responses in range";
    return `${Math.ceil(Math.max(0, targetCompletes - responsesInRange) / rate)} days`;
  }, [responsesInRange, selectedTimeRange, targetCompletes]);

  const estimatedDate = useMemo(() => {
    if (!responsesInRange) return "Not enough data";
    const hours = (selectedTimeRange[1] - selectedTimeRange[0]) / (3600 * 1000);
    const rate = responsesInRange / hours;
    if (!rate) return "No responses in range";
    return dayjs().add(Math.max(0, targetCompletes - responsesInRange) / rate, "hour").format("YYYY-MM-DD HH:mm");
  }, [responsesInRange, selectedTimeRange, targetCompletes]);

  if (!survey) return <Typography sx={{ mt: 4, ml: 4 }}>Loading...</Typography>;

  return (
    <Container>
      <Grid2 container justifyContent="flex-start" sx={{ mt: 2 }}>
        <Breadcrumbs separator=">">
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>Home</Link>
          <Link to={`/project/${survey.project}`} style={{ textDecoration: "none", color: "inherit" }}>{survey.project_name}</Link>
          <Link to={`/survey/${surveyId}`} style={{ textDecoration: "none", color: "inherit" }}>{survey.name}</Link>
          <Typography color="textPrimary">Survey Pace</Typography>
        </Breadcrumbs>
      </Grid2>

      <Grid2 container justifyContent="center" textAlign="center" sx={{ mt: 2 }}>
        {fetching && (
          <Box sx={{ width: "75%", mt: 3 }}>
            <Typography variant="h4">Fetching survey responses...</Typography>
            <LinearProgress variant="determinate" value={totalResponses ? (progress / totalResponses) * 100 : 0} />
            <Typography variant="body2" sx={{ mt: 1 }}>{progress} / {totalResponses} responses loaded</Typography>
          </Box>
        )}

        {!fetching && completionTimes.length > 0 && (
          <Box sx={{ textAlign: "center", mt: 4, width: "100%" }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Survey Progress</Typography>
            <Box sx={{ width: "80%", height: 300, mx: "auto" }}>
              <Line data={processData(completionTimes)} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, annotation: { annotations: { box1: { type: "box", xMin: dayjs(selectedTimeRange[0]).format("YYYY-MM-DD HH:00"), xMax: dayjs(selectedTimeRange[1]).format("YYYY-MM-DD HH:00"), yMin: 0, yMax: completionTimes.length, backgroundColor: "rgba(255,99,132,0.25)" } } } },
                scales: { x: { type: "time", title: { display: true, text: "Time" } }, y: { title: { display: true, text: "Cumulative Responses" } } },
              }} />
            </Box>

            <Box sx={{ width: "80%", mx: "auto", mt: 3 }}>
              <Typography variant="h6">Survey Completion Estimator</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, justifyContent: "center" }}>
                <Typography>Target Completes:</Typography>
                <TextField type="number" value={targetCompletes} onChange={(e) => setTargetCompletes(Number(e.target.value))} inputProps={{ min: 1 }} sx={{ width: 120 }} />
              </Box>
              <Typography sx={{ mt: 2 }}>Adjust Time Range:</Typography>
              <Slider
                value={selectedTimeRange}
                min={startTs} max={endTs}
                step={Math.max((endTs - startTs) / 100, 3600000)}
                valueLabelFormat={(v) => new Date(v).toLocaleString()}
                onChange={(_, val) => setSelectedTimeRange(val)}
                valueLabelDisplay="auto"
              />
              <Typography variant="h6" sx={{ mt: 2 }}>Estimated: {estimatedDays} ({estimatedDate})</Typography>
            </Box>
          </Box>
        )}
      </Grid2>
    </Container>
  );
};

export default SurveyPace;
